import React, { useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import DOMPurify from 'dompurify';

interface AccordionItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  question,
  answer,
  isOpen,
  onToggle,
  index
}) => {
  const headingId = `accordion-heading-${index}`;
  const contentId = `accordion-content-${index}`;

  const sanitizedAnswer = useMemo(() => DOMPurify.sanitize(answer), [answer]);

  return (
    <div className="bg-white rounded-card shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 overflow-hidden">
      <h2 className="m-0">
        <button
          onClick={onToggle}
          className="flex items-center justify-between w-full text-left p-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
          aria-expanded={isOpen}
          aria-controls={contentId}
          id={headingId}
        >
          <span className="text-xl font-semibold pr-6">{question}</span>
          {isOpen ? (
            <ChevronUp size={24} className="text-primary flex-shrink-0" aria-hidden="true" />
          ) : (
            <ChevronDown size={24} className="text-primary flex-shrink-0" aria-hidden="true" />
          )}
        </button>
      </h2>
      
      <div 
        id={contentId}
        role="region"
        aria-labelledby={headingId}
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div 
          className="px-6 pb-6 prose max-w-none"
          dangerouslySetInnerHTML={{ __html: sanitizedAnswer }}
        />
      </div>
    </div>
  );
};

export default AccordionItem;
